import { useEffect, useState, useCallback } from "react";
import MetaTags from "react-meta-tags";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { useDispatch } from "react-redux";
import jwt_decode from "jwt-decode";
import FeatherIcon from "feather-icons-react";
import Select from "react-select";
import { Form, FormGroup, Input, Label } from "reactstrap";
import { setLoading } from "../../../store/actions";
import { getPropertyList } from "../../Inventory/pages/FacilityInventory/Helpers/Api";
import {
  getInvoices,
  getInvoiceCategories,
  getInvoiceStatusCounts,
  formatCurrency,
  INVOICE_STATUSES,
} from "./Components/Api";
import { InvoicesTable } from "./Components/InvoiceTable";
import { InvoiceCategoriesTable } from "./Components/InvoiceCategoryTable";
import "./Components/invoice.scss";

const TABS = { INVOICES: "invoices", CATEGORIES: "categories" };

const INVOICE_STATUS_BUTTONS = [
  { value: "all", label: "All", icon: "list", color: "#475569" },
  { value: "Unpaid", label: "Unpaid", icon: "alert-circle", color: "#DC2626" },
  { value: "Pending Approval", label: "Pending Approval", icon: "clock", color: "#D97706" },
  { value: "Approved", label: "Approved", icon: "thumbs-up", color: "#0284C7" },
  { value: "Paid", label: "Paid", icon: "check-circle", color: "#059669" },
  { value: "Overdue", label: "Overdue", icon: "alert-triangle", color: "#B91C1C" },
  { value: "Disputed", label: "Disputed", icon: "flag", color: "#7C3AED" },
  { value: "Cancelled", label: "Cancelled", icon: "x-circle", color: "#64748B" },
];

const MONTH_OPTIONS = [
  { value: "", label: "All Months" },
  ...["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map(
    (m, i) => ({ value: String(i + 1), label: m })
  ),
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [
  { value: "", label: "All Years" },
  ...Array.from({ length: 6 }, (_, i) => {
    const y = currentYear - i;
    return { value: String(y), label: String(y) };
  }),
];

const selectStyles = {
  control: (base, { isFocused, isDisabled }) => ({
    ...base,
    borderColor: isFocused ? "#4F46E5" : "#E2E8F0",
    borderRadius: 8,
    minHeight: 34,
    fontSize: "0.80rem",
    boxShadow: isFocused ? "0 0 0 3px rgba(79,70,229,0.12)" : "none",
    minWidth: 140,
    backgroundColor: isDisabled ? "#f8f9fa" : base.backgroundColor,
    cursor: isDisabled ? "not-allowed" : "default",
    "&:hover": { borderColor: "#4F46E5" },
  }),
  option: (base, { isSelected, isFocused }) => ({
    ...base,
    fontSize: "0.80rem",
    background: isSelected ? "#4F46E5" : isFocused ? "#EEF2FF" : "white",
    color: isSelected ? "white" : "#0F172A",
  }),
  menu: (base) => ({ ...base, borderRadius: 8, border: "1px solid #E2E8F0", zIndex: 999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const emptyFilters = {
  propertyId: "",
  buildingName: "",
  year: "",
  month: "",
  status: "all",
  categoryId: "",
  vendorName: "",
};

const Invoice = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(TABS.INVOICES);
  const [user, setUser] = useState(null);
  const [properties, setProperties] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [filters, setFilters] = useState({ ...emptyFilters });
  const [statusData, setStatusData] = useState({ counts: {}, totals: {} });

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem("accessToken");
      return token ? jwt_decode(token) : null;
    } catch { return null; }
  }, []);

  const fetchProperties = useCallback(async () => {
    const currentUser = getUserFromToken();
    if (!currentUser) return;
    setUser(currentUser);
    try {
      dispatch(setLoading(true));
      const result = await getPropertyList(currentUser?.role || "", currentUser?.id || "");
      setProperties(result?.propertyList || []);
    } catch (err) {
      console.error("Failed to fetch properties:", err);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, getUserFromToken]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await getInvoiceCategories({ activeOnly: "true" });
      setCategoryOptions((res?.data || []).map((c) => ({ value: c._id, label: c.name })));
    } catch (err) {
      console.error("Failed to fetch invoice categories:", err);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    setTableLoading(true);
    try {
      const params = {};
      if (filters.propertyId)   params.propertyId   = filters.propertyId;
      if (filters.buildingName) params.buildingName = filters.buildingName;
      if (filters.year)         params.year         = filters.year;
      if (filters.month)        params.month        = filters.month;
      if (filters.status && filters.status !== "all") params.status = filters.status;
      if (filters.categoryId)   params.categoryId   = filters.categoryId;
      if (filters.vendorName)   params.vendorName   = filters.vendorName;
      const res = await getInvoices(params);
      setInvoices(res?.data || []);
    } catch (err) {
      console.error("Failed to fetch invoices:", err);
      setInvoices([]);
    } finally {
      setTableLoading(false);
    }
  }, [filters]);

  const fetchStatusData = useCallback(async () => {
    try {
      const params = {};
      if (filters.propertyId)   params.propertyId   = filters.propertyId;
      if (filters.buildingName) params.buildingName = filters.buildingName;
      if (filters.year)         params.year         = filters.year;
      if (filters.month)        params.month        = filters.month;
      if (filters.categoryId)   params.categoryId   = filters.categoryId;
      if (filters.vendorName)   params.vendorName   = filters.vendorName;
      const res = await getInvoiceStatusCounts(params);
      setStatusData(res?.data || { counts: {}, totals: {} });
    } catch (err) {
      console.error("Failed to fetch invoice status data:", err);
      setStatusData({ counts: {}, totals: {} });
    }
  }, [filters]);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => {
    if (activeTab === TABS.INVOICES) {
      fetchInvoices();
      fetchStatusData();
    }
  }, [fetchInvoices, fetchStatusData, activeTab]);

  // Auto-select building when property has exactly one building
  useEffect(() => {
    if (!filters.propertyId) return;
    const selected = properties.find((p) => p._id === filters.propertyId);
    if (!selected) return;
    const valid = (selected.building || []).filter((b) => b?.buildingName);
    if (valid.length === 1) {
      setFilters((prev) => ({ ...prev, buildingName: valid[0].buildingName }));
    } else {
      const belongs = valid.some((b) => b.buildingName === filters.buildingName);
      if (!belongs) setFilters((prev) => ({ ...prev, buildingName: "" }));
    }
  }, [filters.propertyId, properties]); // eslint-disable-line

  const activeFilterCount = [
    filters.propertyId, filters.categoryId, filters.month, filters.year,
    filters.vendorName,
    filters.status !== "all" ? filters.status : "",
  ].filter(Boolean).length;

  const propertyOptions = [
    { value: "", label: "All Properties" },
    ...(properties || []).map((p) => ({ value: p._id, label: p.title || p.name || p._id })),
  ];

  const buildingOptions = (() => {
    if (!filters.propertyId) return [{ value: "", label: "All Buildings" }];
    const prop = properties.find((p) => p._id === filters.propertyId);
    const buildings = (prop?.building || []).filter((b) => b?.buildingName);
    return [
      { value: "", label: "All Buildings" },
      ...buildings.map((b) => ({ value: b.buildingName, label: b.buildingName })),
    ];
  })();

  const hasSingleBuilding = (() => {
    if (!filters.propertyId) return false;
    const prop = properties.find((p) => p._id === filters.propertyId);
    return ((prop?.building || []).filter((b) => b?.buildingName)).length === 1;
  })();

  const allCategoryOptions = [{ value: "", label: "All Categories" }, ...categoryOptions];

  const tabStyle = (tab) => ({
    cursor: "pointer",
    padding: "9px 22px",
    fontWeight: 700,
    fontSize: "0.85rem",
    color: activeTab === tab ? "#4F46E5" : "#64748B",
    borderBottom: activeTab === tab ? "2px solid #4F46E5" : "2px solid transparent",
    background: "none",
    border: "none",
    display: "flex",
    alignItems: "center",
    gap: 6,
    transition: "all 0.15s ease",
  });

  const grandTotal = statusData.totals?.all || 0;
  const outstandingTotal =
    (statusData.totals?.Unpaid || 0) +
    (statusData.totals?.["Pending Approval"] || 0) +
    (statusData.totals?.Approved || 0) +
    (statusData.totals?.Overdue || 0);
  const paidTotal = statusData.totals?.Paid || 0;

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Invoices</title>
      </MetaTags>
      <div className="container-fluid">
        <Breadcrumbs title="Finance" breadcrumbItem="Invoices" />

        {/* Tabs */}
        <div style={{
          display: "flex", alignItems: "center", marginBottom: 18,
          borderBottom: "1px solid #E2E8F0", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <button style={tabStyle(TABS.INVOICES)} onClick={() => setActiveTab(TABS.INVOICES)}>
              <FeatherIcon icon="file-text" size={14} /> Invoices
            </button>
            <button style={tabStyle(TABS.CATEGORIES)} onClick={() => setActiveTab(TABS.CATEGORIES)}>
              <FeatherIcon icon="layers" size={14} /> Categories
            </button>
          </div>
        </div>

        {activeTab === TABS.INVOICES && (
          <>
            {/* Summary Cards */}
            <div className="iv-summary-grid">
              <div className="iv-summary-card iv-summary-card--all">
                <div className="iv-summary-card__icon"><FeatherIcon icon="hash" size={16} /></div>
                <div>
                  <div className="iv-summary-card__label">Total Invoices</div>
                  <div className="iv-summary-card__value">{statusData.counts?.all || 0}</div>
                  <div className="iv-summary-card__sub">{formatCurrency(grandTotal)}</div>
                </div>
              </div>
              <div className="iv-summary-card iv-summary-card--outstanding">
                <div className="iv-summary-card__icon"><FeatherIcon icon="alert-circle" size={16} /></div>
                <div>
                  <div className="iv-summary-card__label">Outstanding</div>
                  <div className="iv-summary-card__value">{formatCurrency(outstandingTotal)}</div>
                  <div className="iv-summary-card__sub">
                    {(statusData.counts?.Unpaid || 0) +
                      (statusData.counts?.["Pending Approval"] || 0) +
                      (statusData.counts?.Approved || 0) +
                      (statusData.counts?.Overdue || 0)} invoices
                  </div>
                </div>
              </div>
              <div className="iv-summary-card iv-summary-card--paid">
                <div className="iv-summary-card__icon"><FeatherIcon icon="check-circle" size={16} /></div>
                <div>
                  <div className="iv-summary-card__label">Paid</div>
                  <div className="iv-summary-card__value">{formatCurrency(paidTotal)}</div>
                  <div className="iv-summary-card__sub">{statusData.counts?.Paid || 0} invoices</div>
                </div>
              </div>
              <div className="iv-summary-card iv-summary-card--overdue">
                <div className="iv-summary-card__icon"><FeatherIcon icon="alert-triangle" size={16} /></div>
                <div>
                  <div className="iv-summary-card__label">Overdue</div>
                  <div className="iv-summary-card__value">{formatCurrency(statusData.totals?.Overdue || 0)}</div>
                  <div className="iv-summary-card__sub">{statusData.counts?.Overdue || 0} invoices</div>
                </div>
              </div>
            </div>

            {/* Status radio row */}
            <Form className="mb-3">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 18 }}>
                {INVOICE_STATUS_BUTTONS.map((s) => (
                  <FormGroup check inline key={s.value} style={{ marginBottom: 0 }}>
                    <Input
                      type="radio"
                      name="invStatus"
                      checked={filters.status === s.value}
                      onChange={() => setFilters((f) => ({ ...f, status: s.value }))}
                    />
                    <Label check style={{ color: s.color, fontWeight: 600, fontSize: "0.83rem", cursor: "pointer" }}>
                      <FeatherIcon icon={s.icon} size={14} style={{ marginRight: 4, verticalAlign: "middle" }} />
                      {s.label}
                      {statusData.counts?.[s.value] !== undefined && (
                        <span style={{
                          marginLeft: 5, background: "#F1F5F9", color: "#475569",
                          borderRadius: 20, padding: "1px 7px",
                          fontSize: "0.72rem", fontWeight: 700,
                        }}>
                          {statusData.counts[s.value]}
                        </span>
                      )}
                    </Label>
                  </FormGroup>
                ))}
              </div>
            </Form>

            {/* Filter bar */}
            <div style={{
              background: "white", border: "1px solid #E2E8F0", borderRadius: 12,
              padding: "14px 18px", marginBottom: 16, display: "flex",
              flexWrap: "wrap", gap: 12, alignItems: "center",
              boxShadow: "0 1px 4px rgba(15,23,42,0.05)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginRight: 4 }}>
                <FeatherIcon icon="filter" size={14} color="#4F46E5" />
                <span style={{
                  fontSize: "0.78rem", fontWeight: 700, color: "#0F172A",
                  textTransform: "uppercase", letterSpacing: "0.5px",
                }}>Filters</span>
              </div>

              <div style={{ minWidth: 180 }}>
                <Select
                  options={propertyOptions}
                  value={propertyOptions.find((o) => o.value === filters.propertyId) || propertyOptions[0]}
                  onChange={(opt) => setFilters((f) => ({ ...f, propertyId: opt?.value || "", buildingName: "" }))}
                  styles={selectStyles} placeholder="All Properties"
                  menuPortalTarget={document.body} menuPosition="fixed"
                />
              </div>

              <div style={{ minWidth: 180 }}>
                <Select
                  options={buildingOptions}
                  value={buildingOptions.find((o) => o.value === filters.buildingName) || buildingOptions[0]}
                  onChange={(opt) => setFilters((f) => ({ ...f, buildingName: opt?.value || "" }))}
                  styles={selectStyles} placeholder="All Buildings"
                  isDisabled={!filters.propertyId || hasSingleBuilding}
                  menuPortalTarget={document.body} menuPosition="fixed"
                />
              </div>

              <div style={{ minWidth: 160 }}>
                <Select
                  options={allCategoryOptions}
                  value={allCategoryOptions.find((o) => o.value === filters.categoryId) || allCategoryOptions[0]}
                  onChange={(opt) => setFilters((f) => ({ ...f, categoryId: opt?.value || "" }))}
                  styles={selectStyles} placeholder="All Categories"
                  menuPortalTarget={document.body} menuPosition="fixed"
                />
              </div>

              <div style={{ minWidth: 130 }}>
                <Select
                  options={MONTH_OPTIONS}
                  value={MONTH_OPTIONS.find((m) => m.value === filters.month) || MONTH_OPTIONS[0]}
                  onChange={(opt) => setFilters((f) => ({ ...f, month: opt?.value || "" }))}
                  styles={selectStyles} placeholder="All Months"
                  menuPortalTarget={document.body} menuPosition="fixed"
                />
              </div>

              <div style={{ minWidth: 120 }}>
                <Select
                  options={YEAR_OPTIONS}
                  value={YEAR_OPTIONS.find((y) => y.value === filters.year) || YEAR_OPTIONS[0]}
                  onChange={(opt) => setFilters((f) => ({ ...f, year: opt?.value || "" }))}
                  styles={selectStyles} placeholder="All Years"
                  menuPortalTarget={document.body} menuPosition="fixed"
                />
              </div>

              <div style={{ minWidth: 180 }}>
                <Input
                  type="text"
                  bsSize="sm"
                  placeholder="Search vendor…"
                  value={filters.vendorName}
                  onChange={(e) => setFilters((f) => ({ ...f, vendorName: e.target.value }))}
                  style={{
                    borderRadius: 8, fontSize: "0.80rem", borderColor: "#E2E8F0", height: 34,
                  }}
                />
              </div>

              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={() => setFilters({ ...emptyFilters })}
                  style={{
                    background: "#FEF2F2", border: "1px solid #FECDD3", borderRadius: 7,
                    color: "#DC2626", fontSize: "0.75rem", fontWeight: 600,
                    padding: "5px 12px", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 5,
                  }}
                >
                  <FeatherIcon icon="x" size={12} /> Clear
                </button>
              )}

              {activeFilterCount > 0 && (
                <span style={{
                  background: "#4F46E5", color: "white", borderRadius: 20,
                  padding: "2px 9px", fontSize: "0.72rem", fontWeight: 700,
                }}>
                  {activeFilterCount} active
                </span>
              )}
            </div>

            <InvoicesTable
              invoices={invoices}
              loading={tableLoading}
              properties={properties}
              categoryOptions={categoryOptions}
              userRole={user?.role || ""}
              onRefresh={() => { fetchInvoices(); fetchStatusData(); }}
              filters={filters}
              statusOptions={INVOICE_STATUSES}
            />
          </>
        )}

        {activeTab === TABS.CATEGORIES && (
          <InvoiceCategoriesTable userRole={user?.role || ""} onRefresh={fetchCategories} />
        )}
      </div>
    </div>
  );
};

export { Invoice };
